/** Chip pickers shared by the marketing onboarding form (member + staff fill-in). */
import React, { useState } from 'react';
import { Plus, X } from 'lucide-react';

export function ChipGroup({ options, value, onChange, multi = true, testId }) {
  const selected = multi ? value || [] : value ? [value] : [];
  const toggle = (k) => {
    if (!multi) return onChange(k);
    return onChange(selected.includes(k) ? selected.filter((x) => x !== k) : [...selected, k]);
  };
  return (
    <div className="flex flex-wrap gap-2" data-testid={testId}>
      {Object.entries(options).map(([k, label]) => {
        const on = selected.includes(k);
        return (
          <button key={k} type="button" onClick={() => toggle(k)} data-testid={`${testId}-${k}`} aria-pressed={on}
            className={`rounded-full border px-3.5 py-1.5 text-sm font-semibold transition-colors ${on ? 'bg-[#006400] border-[#006400] text-white' : 'bg-white border-slate-300 text-slate-700 hover:border-[#006400] hover:text-[#006400]'}`}>
            {label}
          </button>
        );
      })}
    </div>
  );
}

export function TagInput({ value, onChange, placeholder, testId }) {
  const [v, setV] = useState('');
  const add = () => {
    const parts = v.split(/[,\n]/).map((x) => x.trim()).filter(Boolean);
    if (!parts.length) return;
    const next = [...(value || [])];
    parts.forEach((p) => { if (!next.some((x) => x.toLowerCase() === p.toLowerCase())) next.push(p); });
    onChange(next);
    setV('');
  };
  return (
    <div data-testid={testId}>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {(value || []).map((t) => (
          <span key={t} className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-900 px-2.5 py-1 text-sm" data-testid={`${testId}-item`}>
            {t}<button type="button" onClick={() => onChange(value.filter((x) => x !== t))} className="text-emerald-700 hover:text-rose-600" aria-label={`Remove ${t}`}><X className="w-3.5 h-3.5" /></button>
          </span>
        ))}
        {!(value || []).length && <span className="text-sm text-slate-400 italic">None added yet</span>}
      </div>
      <div className="flex gap-2">
        <input value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} onBlur={add}
          placeholder={placeholder} className="h-10 flex-1 rounded-lg border border-slate-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#006400]/30" data-testid={`${testId}-input`} />
        <button type="button" onClick={add} className="h-10 w-10 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 flex items-center justify-center" aria-label="Add" data-testid={`${testId}-add`}><Plus className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

export function Field({ n, title, hint, children }) {
  return (
    <section className="rounded-2xl bg-white border border-slate-200 p-5 sm:p-6">
      <div className="flex items-start gap-3 mb-4">
        <span className="w-7 h-7 rounded-full bg-[#006400] text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{n}</span>
        <div>
          <h2 className="text-base md:text-lg font-bold text-slate-900">{title}</h2>
          {hint && <p className="text-sm text-slate-500 mt-0.5">{hint}</p>}
        </div>
      </div>
      {children}
    </section>
  );
}
